import React, {ReactNode, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {css as pandaCss} from "@panda/css";

import {CenterBox} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, m, mb, p, pb, pr, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {AdvancedScroll} from "@pg-mono/advanced-scroll-module";
import {useScrollableListItemTracking} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../app/rp_reducer";
import {PropertyItem} from "../../../../atoms/PropertyItem";
import {notifyBugsnag} from "../../../../errors/bugsnag/notify_bugsnag";
import {hitMultileadRecommendationShown} from "../../../../lead/tracking/hit_multilead_recommendation_shown";
import {MultileadVersion} from "../../../../lead/types/MultileadVersion";
import {RecommendationSource} from "../../../../lead/types/RecommendationSource";
import {useGetPropertyDetailQuery} from "../../../../property/api/get_property_detail";
import {IRecommendedProperty} from "../../../../property/types/IRecommendedProperty";
import {PropertyBoxType} from "../../../../property/types/PropertyBoxType";
import {getRecommendationFromProperty} from "../../../../recommendations/utils/get_recommendation_from_property";
import {Country} from "../../../../region/types/Country";
import {ViewType} from "../../../../view_type/ViewType";
import {MultiLeadRecommendationType} from "../../../types/MultiLeadRecommendationType";
import {MultiLeadSimpleRecommendationsMap} from "./MultiLeadSimpleRecommendationsMap";

interface IProps {
    listTopSlot?: ReactNode;
    mainPropertyId?: number;
    allowToSelectMainProperty?: boolean;
    recommendedProperties: IRecommendedProperty[];
    onListScroll: (event: React.UIEvent) => void;
    isScrolledToBottom: boolean;
    selectedPropertyIds: number[];
    handleChange: (viewType: ViewType, propertyId: number) => void;
    originalApplicationGeoPoint: {coordinates: [number, number]} | null | undefined;
    originalApplicationCountry?: Country;
    originalApplicationEstimatedPrice?: string | null;
    handlePinClick: (viewType: ViewType | null, recommendedItemID: number) => void;
}

export const MultiLeadSimpleRecommendationsPropertyList = (props: IProps) => {
    const [hoveredItemID, setHoveredItemID] = useState<null | number>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const listWrap = useRef<HTMLUListElement>(null);
    const viewType = useSelector((store: IRPStore) => store.viewType.current);
    const {isMobile} = useUserDevice();

    const allowToSelectMainProperty = !!props.allowToSelectMainProperty;
    const {data: mainPropertyDetail, isLoading: isLoadingMainPropertyDetail} = useGetPropertyDetailQuery(props.mainPropertyId!, {
        skip: !props.mainPropertyId
    });

    const ignoredCn = useScrollableListItemTracking({
        scrollableListRef: listRef,
        directAncestorListRef: listWrap,
        listElementTrackingIdDatasetName: "trackId",
        handleListItemTrackingHit: (trackingId) => {
            hitMultileadRecommendationShown({
                recommendationId: trackingId,
                recommendationType: MultiLeadRecommendationType.PROPERTY,
                recommendationSource: RecommendationSource.DISTANCE,
                multileadVersion: MultileadVersion.RP_LIST_PROPERTY,
                viewType
            });
        },
        onError: (error) => {
            console.warn(error);
            notifyBugsnag(new Error(error), error);
        }
    });

    if (isLoadingMainPropertyDetail) {
        return (
            <CenterBox>
                <Loader size="md" />
            </CenterBox>
        );
    }

    const onListItemHover = (itemID: number | null) => {
        setHoveredItemID(itemID);
    };

    return (
        <div>
            <div css={modalWrapper}>
                <AdvancedScroll
                    ref={listRef}
                    height={440}
                    contentClassName={leftSectionScrollContentPandaStyle}
                    scrollTopShadowClassName={contentTopShadowPandaStyle}
                    scrollBottomShadowClassName={contentBottomShadowPandaStyle}
                    disableScroll={!!isMobile}
                    css={[leftSectionWrapper, w100]}
                >
                    <ListWrapper onScroll={props.onListScroll} isScrolledToBottom={props.isScrolledToBottom} ref={listWrap}>
                        <div className={ignoredCn}>{props.listTopSlot}</div>
                        {allowToSelectMainProperty && mainPropertyDetail ? (
                            <PropertyItem
                                data-track-id={mainPropertyDetail.id}
                                property={getRecommendationFromProperty(mainPropertyDetail)}
                                checked={props.selectedPropertyIds.includes(mainPropertyDetail.id)}
                                onHover={onListItemHover}
                                handleChange={() => props.handleChange(ViewType.PROPERTY, mainPropertyDetail.id)}
                                type={PropertyBoxType.MAIN}
                            />
                        ) : null}

                        {[...props.recommendedProperties]
                            .sort((a, b) => (a.price || 0) - (b.price || 0))
                            .map((recommendedItem) => {
                                const isChecked = props.selectedPropertyIds.includes(recommendedItem.id);
                                const hasPriceComparison = !!recommendedItem.price && !!mainPropertyDetail?.price;
                                const hasAreaComparison = !!mainPropertyDetail?.area && !!recommendedItem.area;

                                const recommendationAdvantages =
                                    hasPriceComparison || hasAreaComparison
                                        ? {
                                              priceDifference: hasPriceComparison ? mainPropertyDetail.price! - recommendedItem.price! : 0,
                                              areaDifference: hasAreaComparison ? mainPropertyDetail.area - recommendedItem.area : 0
                                          }
                                        : undefined;

                                return (
                                    <PropertyItem
                                        key={recommendedItem.id}
                                        recommendationAdvantages={recommendationAdvantages}
                                        data-track-id={recommendedItem.id}
                                        property={recommendedItem}
                                        checked={isChecked}
                                        onHover={onListItemHover}
                                        handleChange={() => props.handleChange(ViewType.PROPERTY, recommendedItem.id)}
                                        type={PropertyBoxType.RECOMMENDED}
                                    />
                                );
                            })}
                    </ListWrapper>
                </AdvancedScroll>

                {props.originalApplicationGeoPoint && props.originalApplicationCountry && (
                    <div css={mapWrapperStyle}>
                        <MultiLeadSimpleRecommendationsMap
                            hoveredItemID={hoveredItemID}
                            originalGeoPoint={props.originalApplicationGeoPoint!}
                            originalCountry={props.originalApplicationCountry!}
                            originalOfferLabel={props.originalApplicationEstimatedPrice}
                            viewType={viewType}
                            handlePinClick={props.handlePinClick}
                            recommendations={props.recommendedProperties}
                            recommendationType={MultiLeadRecommendationType.PROPERTY}
                            checkedList={props.selectedPropertyIds}
                            css={mapStyle}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

interface IListWrapperProps {
    isScrolledToTop?: boolean;
    isScrolledToBottom?: boolean;
}

export const ListWrapper = styled.ul<IListWrapperProps>`
    ${m(0)};
    ${p(0)};
    list-style: none;
    position: relative;
    flex-basis: calc(50% - 4.8rem);

    @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
        ${pr(5)};
        ${pb(2)};
        max-height: inherit;
    }
`;

const leftSectionWrapper = (props: Theme) => css`
    ${mb(2)};

    @media (min-width: ${props.breakpoints.sm}) {
        ${mb(0)};
        ${pb(2)};
    }
`;

const modalWrapper = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.sm}) {
        justify-content: space-between;
        gap: ${calculateRemSize(1)};
        flex-direction: row;
    }
`;

const mapWrapperStyle = (theme: Theme) => css`
    ${mb(5)};
    position: relative;
    width: 100%;

    @media (min-width: ${theme.breakpoints.sm}) {
        ${mb(0)};
        height: 440px;
        flex: 0 0 50%;
    }
`;

const mapStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        overflow: hidden;
        ${borderRadius(1)};
    }
`;

// Panda CSS styles
const leftSectionScrollContentPandaStyle = pandaCss({
    pl: 1.5,
    md: {
        pl: 0
    }
});

const contentTopShadowPandaStyle = pandaCss({
    background: "linear-gradient(rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))"
});

const contentBottomShadowPandaStyle = pandaCss({
    background: "linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))"
});
import React, {ReactNode, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {css as pandaCss} from "@panda/css";

import {CenterBox} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, m, mb, p, pb, pr, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {AdvancedScroll} from "@pg-mono/advanced-scroll-module";
import {useScrollableListItemTracking} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../app/rp_reducer";
import {PropertyItem} from "../../../../atoms/PropertyItem";
import {notifyBugsnag} from "../../../../errors/bugsnag/notify_bugsnag";
import {hitMultileadRecommendationShown} from "../../../../lead/tracking/hit_multilead_recommendation_shown";
import {MultileadVersion} from "../../../../lead/types/MultileadVersion";
import {RecommendationSource} from "../../../../lead/types/RecommendationSource";
import {useGetPropertyDetailQuery} from "../../../../property/api/get_property_detail";
import {IRecommendedProperty} from "../../../../property/types/IRecommendedProperty";
import {PropertyBoxType} from "../../../../property/types/PropertyBoxType";
import {getRecommendationFromProperty} from "../../../../recommendations/utils/get_recommendation_from_property";
import {Country} from "../../../../region/types/Country";
import {ViewType} from "../../../../view_type/ViewType";
import {MultiLeadRecommendationType} from "../../../types/MultiLeadRecommendationType";
import {MultiLeadSimpleRecommendationsMap} from "./MultiLeadSimpleRecommendationsMap";

interface IProps {
    listTopSlot?: ReactNode;
    mainPropertyId?: number;
    allowToSelectMainProperty?: boolean;
    recommendedProperties: IRecommendedProperty[];
    onListScroll: (event: React.UIEvent) => void;
    isScrolledToBottom: boolean;
    selectedPropertyIds: number[];
    handleChange: (viewType: ViewType, propertyId: number) => void;
    originalApplicationGeoPoint: {coordinates: [number, number]} | null | undefined;
    originalApplicationCountry?: Country;
    originalApplicationEstimatedPrice?: string | null;
    handlePinClick: (viewType: ViewType | null, recommendedItemID: number) => void;
}

export const MultiLeadSimpleRecommendationsPropertyList = (props: IProps) => {
    const [hoveredItemID, setHoveredItemID] = useState<null | number>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const listWrap = useRef<HTMLUListElement>(null);
    const viewType = useSelector((store: IRPStore) => store.viewType.current);
    const {isMobile} = useUserDevice();

    const allowToSelectMainProperty = !!props.allowToSelectMainProperty;
    const {data: mainPropertyDetail, isLoading: isLoadingMainPropertyDetail} = useGetPropertyDetailQuery(props.mainPropertyId!, {
        skip: !props.mainPropertyId
    });

    const ignoredCn = useScrollableListItemTracking({
        scrollableListRef: listRef,
        directAncestorListRef: listWrap,
        listElementTrackingIdDatasetName: "trackId",
        handleListItemTrackingHit: (trackingId) => {
            hitMultileadRecommendationShown({
                recommendationId: trackingId,
                recommendationType: MultiLeadRecommendationType.PROPERTY,
                recommendationSource: RecommendationSource.DISTANCE,
                multileadVersion: MultileadVersion.RP_LIST_PROPERTY,
                viewType
            });
        },
        onError: (error) => {
            console.warn(error);
            notifyBugsnag(new Error(error), error);
        }
    });

    if (isLoadingMainPropertyDetail) {
        return (
            <CenterBox>
                <Loader size="md" />
            </CenterBox>
        );
    }

    const onListItemHover = (itemID: number | null) => {
        setHoveredItemID(itemID);
    };

    return (
        <div>
            <div css={modalWrapper}>
                <AdvancedScroll
                    ref={listRef}
                    height={440}
                    contentClassName={leftSectionScrollContentPandaStyle}
                    scrollTopShadowClassName={contentTopShadowPandaStyle}
                    scrollBottomShadowClassName={contentBottomShadowPandaStyle}
                    disableScroll={!!isMobile}
                    css={[leftSectionWrapper, w100]}
                >
                    <ListWrapper onScroll={props.onListScroll} isScrolledToBottom={props.isScrolledToBottom} ref={listWrap}>
                        <div className={ignoredCn}>{props.listTopSlot}</div>
                        {allowToSelectMainProperty && mainPropertyDetail ? (
                            <PropertyItem
                                data-track-id={mainPropertyDetail.id}
                                property={getRecommendationFromProperty(mainPropertyDetail)}
                                checked={props.selectedPropertyIds.includes(mainPropertyDetail.id)}
                                onHover={onListItemHover}
                                handleChange={() => props.handleChange(ViewType.PROPERTY, mainPropertyDetail.id)}
                                type={PropertyBoxType.MAIN}
                            />
                        ) : null}

                        {[...props.recommendedProperties]
                            .sort((a, b) => (a.price || 0) - (b.price || 0))
                            .map((recommendedItem) => {
                                const isChecked = props.selectedPropertyIds.includes(recommendedItem.id);
                                const hasPriceComparison = !!recommendedItem.price && !!mainPropertyDetail?.price;
                                const hasAreaComparison = !!mainPropertyDetail?.area && !!recommendedItem.area;

                                const recommendationAdvantages =
                                    hasPriceComparison || hasAreaComparison
                                        ? {
                                              priceDifference: hasPriceComparison ? mainPropertyDetail.price! - recommendedItem.price! : 0,
                                              areaDifference: hasAreaComparison ? mainPropertyDetail.area - recommendedItem.area : 0
                                          }
                                        : undefined;

                                return (
                                    <PropertyItem
                                        key={recommendedItem.id}
                                        recommendationAdvantages={recommendationAdvantages}
                                        data-track-id={recommendedItem.id}
                                        property={recommendedItem}
                                        checked={isChecked}
                                        onHover={onListItemHover}
                                        handleChange={() => props.handleChange(ViewType.PROPERTY, recommendedItem.id)}
                                        type={PropertyBoxType.RECOMMENDED}
                                    />
                                );
                            })}
                    </ListWrapper>
                </AdvancedScroll>

                {props.originalApplicationGeoPoint && props.originalApplicationCountry && (
                    <div css={mapWrapperStyle}>
                        <MultiLeadSimpleRecommendationsMap
                            hoveredItemID={hoveredItemID}
                            originalGeoPoint={props.originalApplicationGeoPoint!}
                            originalCountry={props.originalApplicationCountry!}
                            originalOfferLabel={props.originalApplicationEstimatedPrice}
                            viewType={viewType}
                            handlePinClick={props.handlePinClick}
                            recommendations={props.recommendedProperties}
                            recommendationType={MultiLeadRecommendationType.PROPERTY}
                            checkedList={props.selectedPropertyIds}
                            css={mapStyle}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

interface IListWrapperProps {
    isScrolledToTop?: boolean;
    isScrolledToBottom?: boolean;
}

export const ListWrapper = styled.ul<IListWrapperProps>`
    ${m(0)};
    ${p(0)};
    list-style: none;
    position: relative;
    flex-basis: calc(50% - 4.8rem);

    @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
        ${pr(5)};
        ${pb(2)};
        max-height: inherit;
    }
`;

const leftSectionWrapper = (props: Theme) => css`
    ${mb(2)};

    @media (min-width: ${props.breakpoints.sm}) {
        ${mb(0)};
        ${pb(2)};
    }
`;

const modalWrapper = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.sm}) {
        justify-content: space-between;
        gap: ${calculateRemSize(1)};
        flex-direction: row;
    }
`;

const mapWrapperStyle = (theme: Theme) => css`
    ${mb(5)};
    position: relative;
    width: 100%;

    @media (min-width: ${theme.breakpoints.sm}) {
        ${mb(0)};
        height: 440px;
        flex: 0 0 50%;
    }
`;

const mapStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        overflow: hidden;
        ${borderRadius(1)};
    }
`;

// Panda CSS styles
const leftSectionScrollContentPandaStyle = pandaCss({
    pl: 1.5,
    md: {
        pl: 0
    }
});

const contentTopShadowPandaStyle = pandaCss({
    background: "linear-gradient(rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))"
});

const contentBottomShadowPandaStyle = pandaCss({
    background: "linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))"
});
